import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { safeFormat } from '@/lib/safeDate';
import { ptBR } from 'date-fns/locale';
import { RefreshCcw, Search, Calendar, User, CheckCircle, XCircle, Video } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useConnectionCorrections, MiscorrectedConnection } from '@/hooks/useConnectionCorrections';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { supabase } from '@/integrations/supabase/client';

export default function ConnectionCorrectionSettings() {
  const { isAdmin, isSalesManager, isLoading: permissionsLoading } = useUserPermissions();
  const { connections, isLoading, filters, setFilters, correctConnection, isCorrectingConnection } = useConnectionCorrections();
  
  // Fetch active users for SDR filter
  const { data: activeUsers } = useQuery({
    queryKey: ['active-users-for-filter'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_users')
        .select('id, display_name')
        .eq('active', true)
        .order('display_name');
      if (error) throw error;
      return data || [];
    },
  });

  const [selectedConnection, setSelectedConnection] = useState<MiscorrectedConnection | null>(null);
  const [correctionNotes, setCorrectionNotes] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sdrId, setSdrId] = useState('');
  const [contactSearch, setContactSearch] = useState('');

  // Access control
  if (permissionsLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!isAdmin && !isSalesManager) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-8 text-center">
            <XCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
            <p className="text-lg font-medium">Acesso Restrito</p>
            <p className="text-muted-foreground">
              Apenas administradores e gerentes de vendas podem acessar esta página.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleApplyFilters = () => {
    setFilters({
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      sdrId: sdrId && sdrId !== 'all' ? sdrId : undefined,
      contactSearch: contactSearch || undefined,
    });
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSdrId('');
    setContactSearch('');
    setFilters({});
  };

  const openCorrectionModal = (connection: MiscorrectedConnection) => {
    setSelectedConnection(connection);
    setCorrectionNotes('');
    setIsModalOpen(true);
  };

  const handleConfirmCorrection = () => {
    if (!selectedConnection) return;
    
    correctConnection({
      id: selectedConnection.id,
      source: selectedConnection.source,
      notes: correctionNotes || undefined,
    });
    
    setIsModalOpen(false);
    setSelectedConnection(null);
    setCorrectionNotes('');
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    try {
      return safeFormat(new Date(dateStr), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return '-';
    }
  };

  // Get unique SDRs from active users
  const sdrOptions = activeUsers?.filter(u => u.id && u.display_name) || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <RefreshCcw className="h-6 w-6 text-primary" />
          Correção de Conexões SDR
        </h1>
        <p className="text-muted-foreground mt-1">
          Corrija status de conexões que foram marcadas incorretamente como "Não Conectou"
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Data Início</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Data Fim</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">SDR</Label>
              <Select value={sdrId} onValueChange={setSdrId}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os SDRs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os SDRs</SelectItem>
                  {sdrOptions.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Buscar Contato</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Nome do contato..."
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={handleApplyFilters} className="flex-1">
                Filtrar
              </Button>
              <Button variant="outline" onClick={handleClearFilters}>
                Limpar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <XCircle className="h-5 w-5 text-destructive" />
            Conexões Marcadas como "Não Conectou"
            {connections.length > 0 && (
              <Badge variant="secondary">{connections.length}</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Clique em "Corrigir" para marcar a conexão como confirmada e creditar o SDR
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : connections.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-primary" />
              <p className="text-lg font-medium">Nenhuma correção pendente</p>
              <p className="text-sm">
                Todas as conexões estão com status correto ou não há conexões com os filtros aplicados.
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Contato</TableHead>
                    <TableHead>SDR (Agendou)</TableHead>
                    <TableHead>Host (Marcou "Não")</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Notas</TableHead>
                    <TableHead className="text-right">Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {connections.map((connection) => (
                    <TableRow key={`${connection.source}-${connection.id}`}>
                      <TableCell className="whitespace-nowrap">
                        {formatDate(connection.event_date)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {connection.contact_name || '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          {connection.scheduled_by_name || '-'}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {connection.confirmed_by_name || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="gap-1">
                          {connection.source === 'scheduled' ? (
                            <>
                              <Calendar className="h-3 w-3" />
                              Agendada
                            </>
                          ) : (
                            <>
                              <Video className="h-3 w-3" />
                              Video Now
                            </>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                        {connection.connection_notes || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => openCorrectionModal(connection)}
                          disabled={isCorrectingConnection}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Corrigir
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Correction Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Correção de Conexão</DialogTitle>
            <DialogDescription>
              Esta ação irá marcar a conexão como confirmada e creditar o SDR.
            </DialogDescription>
          </DialogHeader>
          
          {selectedConnection && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Contato</p>
                  <p className="font-medium">{selectedConnection.contact_name || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Data</p>
                  <p className="font-medium">{formatDate(selectedConnection.event_date)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Agendada por (SDR)</p>
                  <p className="font-medium text-primary">{selectedConnection.scheduled_by_name || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Confirmada por</p>
                  <p className="font-medium">{selectedConnection.confirmed_by_name || '-'}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notas da Correção (opcional)</Label>
                <Textarea
                  placeholder="Ex: Clicou 'Não' por engano, reunião aconteceu normalmente..."
                  value={correctionNotes}
                  onChange={(e) => setCorrectionNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmCorrection} 
              disabled={isCorrectingConnection}
            >
              {isCorrectingConnection ? 'Corrigindo...' : 'Confirmar Correção'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
